import {
  AnyFeedItemEntityVersion,
  FeedItem,
  FirestoreEntity,
  Logger,
  Mapper,
} from '@uploadcam/common';
import { CollectionReference, doc, getDoc } from 'firebase/firestore';

export interface GetMirroredFeedItemsResult {
  /** Items with a `publicUrl`. Ordered to match the input `itemIds`. */
  items: FeedItem[];
  /** Item ids the read failed (rule denial or doc missing) — they have no `publicUrl` yet. */
  missing: string[];
}

/**
 * Reads each FeedItem one at a time so a per-doc permission denial only skips that one item.
 *
 * Firestore's `where(documentId(), 'in', [...])` evaluates the read rule per doc but fails the
 * *whole query* on the first denial, which would defeat the purpose of public galleries that
 * contain items still being mirrored. Per-doc reads are slower but tolerate denials cleanly.
 */
export class GetMirroredFeedItemsInteractor {
  constructor(
    private readonly collection: CollectionReference<AnyFeedItemEntityVersion>,
    private readonly logger: Logger,
    private readonly mapper: Mapper<FirestoreEntity<AnyFeedItemEntityVersion>, FeedItem>,
  ) {}

  public async execute(itemIds: readonly string[]): Promise<GetMirroredFeedItemsResult> {
    if (itemIds.length === 0) {
      return { items: [], missing: [] };
    }

    const results = await Promise.allSettled(
      itemIds.map(async (id) => {
        const snap = await getDoc(doc(this.collection, id));
        if (!snap.exists()) return { id, item: undefined };
        return { id, item: this.mapper.map(FirestoreEntity.from(snap)) };
      }),
    );

    const byId = new Map<string, FeedItem>();
    const missing: string[] = [];

    for (let i = 0; i < results.length; i++) {
      const id = itemIds[i];
      const result = results[i];
      const item = result.status === 'fulfilled' ? result.value.item : undefined;
      // Treat items without `publicUrl` as missing too — an authenticated team member can
      // read them through the regular rule, but they're not yet displayable in the gallery.
      if (item && item.publicUrl) {
        byId.set(id, item);
      } else {
        if (result.status === 'rejected') {
          this.logger.warn(`Skipping FeedItem ${id}:`, result.reason);
        }
        missing.push(id);
      }
    }

    // Preserve the gallery's ordering.
    const items = itemIds.map((id) => byId.get(id)).filter((x): x is FeedItem => !!x);
    return { items, missing };
  }
}
